import i18n from '../i18n/config';
// Authentication Service - FIXED: Multi-company support
import apiClient, { api, handleApiError } from './api';

class AuthService {
  constructor() {
    this.TOKEN_KEY = 'authToken';
    this.USER_KEY = 'user';
    this.COMPANIES_KEY = i18n.t('profile.security.reset.companies');
    this.CURRENT_COMPANY_KEY = i18n.t('hardcoded.services_authservice.currentcompany');
  }

  // Login user with optional company selection
  async login(email, password, companyId = null) {
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      if (companyId) {
        formData.append('company_id', companyId);
      }

      const response = await apiClient.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (response.data) {
        this.setAuthData(response.data);
        return { success: true, data: response.data };
      }

      return { success: false, message: 'Invalid response from server' };
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Switch to different company
  async switchCompany(companyId) {
    try {
      const response = await apiClient.post('/auth/switch-company', {
        company_id: companyId
      });

      if (response.data && response.data.access_token) {
        this.setAuthData(response.data);
        return { success: true, data: response.data };
      }

      return { success: false, message: i18n.t('hooks_useauth.failed_switch_company') };
    } catch (error) {
      return handleApiError(error);
    }
  }

  // 🔥 FIXED: Get current user info with companies - Handle different response format
  async getCurrentUser() {
    try {
      const response = await api.auth.getCurrentUser();

      if (response.data) {
        // 🔥 FIXED: Transform /me response to match login format
        const transformedData = {
          user: {
            id: response.data.id,
            email: response.data.email,
            name: response.data.name,
            last_name: response.data.last_name,
            full_name: response.data.full_name,
            phone: response.data.phone,
            language: response.data.language,
            created_at: response.data.created_at,
          },
          companies: response.data.companies || [],
          total_companies: response.data.total_companies || 0
        };

        // Update stored user and company data
        this.updateStoredUser(transformedData);
        return { success: true, data: transformedData };
      }

      return { success: false, message: 'Failed to get user info' };
    } catch (error) {
      console.error('getCurrentUser error:', error);
      return handleApiError(error);
    }
  }

  // Register company with admin user
  async registerCompany(companyData, adminData, servicePlanId, billingPlanId = null, registrationToken = null) {
    try {
      const registrationData = {
        // Company data
        company_name: companyData.company_name,
        rfc: companyData.rfc,
        manager_name: companyData.manager_name,
        manager_email: companyData.manager_email,
        country: companyData.country,
        state: companyData.state || null,
        city: companyData.city || null,
        address: companyData.address || null,
        currency: companyData.currency,
        phone: companyData.phone || null,
        service_plan_id: null,
        billing_plan_id: billingPlanId || servicePlanId || null,
        registration_token: registrationToken || null,

        // Admin user mode
        admin_user_mode: adminData.mode || 'new',

        // Admin user data
        admin_name: adminData.mode === 'existing' ? '' : (adminData.name || ''),
        admin_last_name: adminData.mode === 'existing' ? '' : (adminData.last_name || ''),
        admin_email: adminData.email,
        admin_password: adminData.mode === 'existing' ? '' : (adminData.password || ''),
        admin_phone: adminData.mode === 'existing' ? null : (adminData.phone || null),
        admin_language: adminData.language || 'es',
        admin_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City',
        admin_existing_user_id: adminData.mode === 'existing' ? (adminData.existingUserId || null) : null
      };

      console.log('🔧 Sending registration data:', registrationData);

      const response = await apiClient.post('/auth/register-company', registrationData);

      if (response.data && response.data.access_token) {
        this.setAuthData(response.data);
        return { success: true, data: response.data };
      }

      if (response.data && (response.data.requires_login || response.data.existing_user || response.data.user)) {
        return { success: true, data: response.data };
      }

      return { success: false, message: 'Registration failed' };
    } catch (error) {
      console.error('🚨 Registration error:', error);
      return handleApiError(error);
    }
  }

  // Create smart invitation
  async createSmartInvitation(invitationData) {
    try {
      const response = await api.auth.createSmartInvitation(invitationData);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Use invitation code (for new users or existing users joining new company)
  async useInvitation(invitationCode, userData = null) {
    try {
      const requestData = {
        invitation_code: invitationCode
      };

      // Only include user data if provided (for new users)
      if (userData) {
        Object.assign(requestData, userData);
      }

      const response = await api.auth.useInvitation(requestData);

      if (response.data && response.data.access_token) {
        this.setAuthData(response.data);
        return { success: true, data: response.data };
      }

      return { success: false, message: i18n.t('hooks_useauth.failed_use_invitation') };
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Get service plans
  async getServicePlans() {
    try {
      const response = await api.auth.getServicePlans();
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Get user's companies
  async getUserCompanies() {
    try {
      const response = await api.auth.getCompanies();

      if (response.data) {
        localStorage.setItem(this.COMPANIES_KEY, JSON.stringify(response.data.companies));
        return { success: true, data: response.data };
      }

      return { success: false, message: i18n.t('hooks_useauth.failed_get_companies') };
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Set authentication data
  setAuthData(authData) {
    localStorage.setItem(this.TOKEN_KEY, authData.access_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(authData.user));

    if (authData.companies) {
      localStorage.setItem(this.COMPANIES_KEY, JSON.stringify(authData.companies));
    }

    if (authData.current_company) {
      localStorage.setItem(this.CURRENT_COMPANY_KEY, JSON.stringify(authData.current_company));
    } else if (authData.companies && authData.companies.length > 0) {
      // If no current company specified, use the one from user data
      const userCompany = authData.companies.find(c => c.company_id === authData.user.company_id);
      if (userCompany) {
        localStorage.setItem(this.CURRENT_COMPANY_KEY, JSON.stringify(userCompany));
      } else {
        // If no matching company found, use the first one
        localStorage.setItem(this.CURRENT_COMPANY_KEY, JSON.stringify(authData.companies[0]));
      }
    }
  }

  // 🔥 FIXED: Update stored user data - Handle both formats
  updateStoredUser(userData) {
    // Handle both login format and /me format
    const user = userData.user || userData;
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));

    if (userData.companies) {
      localStorage.setItem(this.COMPANIES_KEY, JSON.stringify(userData.companies));
    }

    // 🔥 FIXED: Update current company if not set or invalid
    if (!this.getCurrentCompany() && userData.companies && userData.companies.length > 0) {
      localStorage.setItem(this.CURRENT_COMPANY_KEY, JSON.stringify(userData.companies[0]));
    }
  }

  // Get stored token
  getToken() {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Get stored user
  getUser() {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  // Get stored companies
  getCompanies() {
    const companiesStr = localStorage.getItem(this.COMPANIES_KEY);
    return companiesStr ? JSON.parse(companiesStr) : [];
  }

  // Get current company
  getCurrentCompany() {
    const companyStr = localStorage.getItem(this.CURRENT_COMPANY_KEY);
    return companyStr ? JSON.parse(companyStr) : null;
  }

  // Set current company
  setCurrentCompany(company) {
    localStorage.setItem(this.CURRENT_COMPANY_KEY, JSON.stringify(company));
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();

    if (!token || !user) {
      return false;
    }

    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      if (payload.exp && payload.exp < currentTime) {
        this.logout();
        return false;
      }

      return true;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  // Check if user has access to specific company
  hasAccessToCompany(companyId) {
    const companies = this.getCompanies();
    return companies.some(c => c.company_id === companyId);
  }

  // Get user role in specific company
  getRoleInCompany(companyId) {
    const companies = this.getCompanies();
    const company = companies.find(c => c.company_id === companyId);
    return company ? company.role : null;
  }

  // Check if user has specific role in current company
  hasRole(role) {
    const currentCompany = this.getCurrentCompany();
    return currentCompany && currentCompany.role === role;
  }

  // Check if user is admin in current company
  isAdmin() {
    return this.hasRole('admin');
  }

  // Check if user is manager or admin in current company
  isManagerOrAdmin() {
    const currentCompany = this.getCurrentCompany();
    return currentCompany && (currentCompany.role === 'admin' || currentCompany.role === 'manager');
  }

  // Check if user has multiple companies
  hasMultipleCompanies() {
    const companies = this.getCompanies();
    return companies.length > 1;
  }

  // Get company by ID
  getCompanyById(companyId) {
    const companies = this.getCompanies();
    return companies.find(c => c.company_id === companyId);
  }

  // Logout user
  logout() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.COMPANIES_KEY);
    localStorage.removeItem(this.CURRENT_COMPANY_KEY);

    // Redirect to login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // 🔥 FIXED: Refresh user data with better error handling
  async refreshUser() {
    try {
      if (!this.isAuthenticated()) {
        return { success: false, message: 'Not authenticated' };
      }

      console.log('🔧 Refreshing user data...');
      const result = await this.getCurrentUser();

      if (result.success) {
        console.log('✅ User data refreshed successfully');
        return result;
      } else {
        console.error('❌ Failed to refresh user data:', result.message);
        // Don't logout on refresh failure, just return the stored data
        const user = this.getUser();
        const companies = this.getCompanies();

        if (user && companies) {
          return {
            success: true,
            data: {
              user,
              companies,
              total_companies: companies.length
            }
          };
        }

        return result;
      }
    } catch (error) {
      console.error('🚨 Error refreshing user data:', error);
      // Return stored data as fallback
      const user = this.getUser();
      const companies = this.getCompanies();

      if (user && companies) {
        return {
          success: true,
          data: {
            user,
            companies,
            total_companies: companies.length
          }
        };
      }

      return { success: false, message: 'Failed to refresh user data' };
    }
  }

  // Setup auth state listeners
  setupAuthListener(callback) {
    // Listen for storage changes (logout from another tab)
    window.addEventListener('storage', (e) => {
      if ([this.TOKEN_KEY, this.USER_KEY, this.COMPANIES_KEY, this.CURRENT_COMPANY_KEY].includes(e.key)) {
        callback(this.isAuthenticated());
      }
    });

    // Periodic token validation
    setInterval(() => {
      if (!this.isAuthenticated()) {
        callback(false);
      }
    }, 60000); // Check every minute
  }

  // Company selection helpers
  async selectCompanyForLogin(email, password, companyId) {
    return await this.login(email, password, companyId);
  }

  // Get company selection data for login
  getCompanySelectionData(companies) {
    return companies.map(company => ({
      id: company.company_id,
      name: company.company_name,
      role: company.role,
      roleDisplay: company.role_display,
      joinedAt: company.joined_at
    }));
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;